export const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

export function isChunkLoadError(error: Error | string): boolean {
  const errorMessage = typeof error === "string" ? error : error.message;
  return (
    errorMessage.includes("dynamically imported module") ||
    errorMessage.includes("Loading chunk") ||
    errorMessage.includes("Loading CSS chunk")
  );
}

// how long the app must run without a chunk failure before the reload-once
// guard is considered stale and cleared, re-arming auto-reload for the next
// deploy hitting this tab
const GUARD_RESET_DELAY_MS = 10_000;

let guardResetTimer: ReturnType<typeof setTimeout> | undefined;

// reload-once recovery for stale hashed chunks after a deploy. Returns true
// when a reload was triggered. While the guard is set, a second failure clears
// it instead of reloading so we never loop; the error then surfaces to the
// caller and the following failure may attempt a reload again.
export function attemptChunkReload(error: Error | string): boolean {
  if (!isChunkLoadError(error)) return false;

  // an active incident must keep the guard: a reset scheduled during the
  // pre-failure lifecycle firing now would re-arm auto-reload mid-incident
  clearTimeout(guardResetTimer);

  const hasAttemptedReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!hasAttemptedReload) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
    globalThis.location.reload();
    return true;
  }

  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  return false;
}

// after a reload the guard stays set so a still-failing chunk shows the manual
// refresh UI instead of looping. once the app has run cleanly for a while the
// guard is stale: clear it so the next deploy gets an auto-reload again.
export function scheduleChunkGuardReset(): void {
  clearTimeout(guardResetTimer);
  guardResetTimer = setTimeout(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }, GUARD_RESET_DELAY_MS);
}
